import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

// Import review slice thunks for cross-slice coordination
import { startReview, approveReview, rejectReview } from './reviewSlice.js';

// ============================================================================
// Async Thunks - API calls to backend
// ============================================================================

/**
 * Fetch all assessments for current user
 * Uses assessment-centric endpoint that returns assessments with model info
 */
export const fetchAllAssessments = createAsyncThunk(
  'assessments/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.includeDeleted) {
        queryParams.append('includeDeleted', 'true');
      }
      const queryString = queryParams.toString();
      const url = `/.netlify/functions/getAssessments${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch assessments`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch assessments');
      }

      return result.assessments;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch a single assessment by UUID
 * Use this instead of fetchAllAssessments when you need to refresh specific assessment data
 * without triggering full re-render of assessment lists
 * @param {string} assessmentUuid - Assessment UUID to fetch
 */
export const fetchSingleAssessment = createAsyncThunk(
  'assessments/fetchSingle',
  async (assessmentUuid, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/getAssessments', {
        method: 'POST',
        body: JSON.stringify({ assessmentUuid }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch assessment`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch assessment');
      }

      return result.assessment; // Single assessment object
    } catch (error) {
      console.error('[assessmentsSlice] fetchSingleAssessment error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create or update an assessment for a model
 * Handles the full flow: check if exists, then create or update with all settings
 */
export const createAssessment = createAsyncThunk(
  'assessments/create',
  async ({ modelId, assessmentName, templateId, mode, autonomous, assessmentMode, currentStep, position, assessmentType, parentAssessmentUuid, appliedRegisterIds }, { rejectWithValue }) => {
    try {
      // Types that allow multiple assessments per model (Builder, Explorer).
      const allowMultiple = assessmentType === 'use_case' || assessmentType === 'business_case';

      let assessmentUuid;

      if (allowMultiple) {
        // For multi-assessment types: always create a new assessment (skip the "check if exists" fetch)
        const createResponse = await authFetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          body: JSON.stringify({
            modelId,
            operation: 'update',
            currentStep,
            position,
            assessmentStatus: 'On-Going',
            assessmentName,
            mode,
            templateId,
            autonomous,
            assessmentMode,
            assessmentType,
            parentAssessmentUuid,  // Builder → Builder UUID, Explorer → Builder UUID
            appliedRegisterIds,    // Step 4 register picker selection
          }),
        });

        if (!createResponse.ok) {
          throw new Error(`HTTP ${createResponse.status}: Failed to create assessment`);
        }

        const createResult = await createResponse.json();
        assessmentUuid = createResult.assessmentUuid;
      } else {
        // Single-assessment types (builder): check if exists first, then create or update
        const checkResponse = await authFetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          body: JSON.stringify({ modelId, operation: 'fetch', assessmentType }),
        });

        if (checkResponse.ok) {
          // Assessment exists, update it
          const existingData = await checkResponse.json();
          assessmentUuid = existingData.assessmentUuid;

          await authFetch('/.netlify/functions/manageAssessment', {
            method: 'POST',
            body: JSON.stringify({
              modelId,
              assessmentUuid,
              operation: 'update',
              assessmentStatus: 'On-Going',
              assessmentName,
              currentStep,
              position,
              mode,
              templateId,
              autonomous,
              assessmentMode,
              assessmentType,
              parentAssessmentUuid,
            }),
          });
        } else {
          // Create new assessment with all settings
          const createResponse = await authFetch('/.netlify/functions/manageAssessment', {
            method: 'POST',
            body: JSON.stringify({
              modelId,
              operation: 'update',
              currentStep,
              position,
              assessmentStatus: 'On-Going',
              assessmentName,
              mode,
              templateId,
              autonomous,
              assessmentMode,
              assessmentType,
              parentAssessmentUuid,
            }),
          });

          if (!createResponse.ok) {
            throw new Error(`HTTP ${createResponse.status}: Failed to create assessment`);
          }

          const createResult = await createResponse.json();
          assessmentUuid = createResult.assessmentUuid;
        }
      }

      // Link any unlinked documents for this model to the new assessment
      // This happens when documents were uploaded during model registration
      try {
        await authFetch('/.netlify/functions/manageAssessmentDocuments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            operation: 'linkToAssessment',
            modelUuid: modelId,
            assessmentUuid,
          }),
        });
      } catch (linkError) {
        // Non-fatal - log but don't fail assessment creation
        console.warn('[createAssessment] Document linking failed:', linkError);
      }

      return {
        assessmentUuid,
        modelId,
        assessmentName,
        templateId,
        mode,
        autonomous,
        currentStep,
        position,
        assessmentStatus: 'On-Going',
        assessmentType: assessmentType || 'builder',
        parentAssessmentUuid: parentAssessmentUuid || null,  // For Explorer assessments
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Initialize/fetch a specific assessment by modelId
 * Uses manageAssessment endpoint with 'fetch' operation
 */
export const initializeAssessment = createAsyncThunk(
  'assessments/initialize',
  async ({ modelId, assessmentType }, { rejectWithValue }) => {
    try {

      // CRITICAL: Must pass assessmentType to ensure we fetch the correct type (Builder vs Explorer)
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({ modelId, operation: 'fetch', assessmentType }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch assessment`);
      }

      const data = await response.json();
      return { assessmentUuid: data.assessmentUuid, modelId, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update assessment status
 */
export const updateAssessmentStatus = createAsyncThunk(
  'assessments/updateStatus',
  async ({ modelId, assessmentStatus }, { rejectWithValue }) => {
    try {

      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          assessmentStatus,
          operation: 'updateStatus',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to update assessment status`);
      }

      const data = await response.json();
      return { modelId, assessmentStatus: data.assessmentStatus };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update assessment position/progress
 */
export const updateAssessmentProgress = createAsyncThunk(
  'assessments/updateProgress',
  async ({ modelId, currentStep, position, assessmentStatus }, { rejectWithValue }) => {
    try {

      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          currentStep,
          position,
          assessmentStatus,
          operation: 'update',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to update assessment progress`);
      }

      const data = await response.json();
      return { assessmentUuid: data.assessmentUuid, currentStep, position, assessmentStatus };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Submit assessment for review
 */
export const submitForReview = createAsyncThunk(
  'assessments/submitForReview',
  async ({ modelId }, { rejectWithValue }) => {
    try {

      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          operation: 'submitForReview',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to submit for review`);
      }

      const data = await response.json();
      return { modelId, reviewStatus: data.reviewStatus };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update review status (for reviewers)
 */
export const updateReviewStatus = createAsyncThunk(
  'assessments/updateReviewStatus',
  async ({ modelId, reviewStatus }, { rejectWithValue }) => {
    try {

      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          reviewStatus,
          operation: 'updateReviewStatus',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to update review status`);
      }

      const data = await response.json();
      return { modelId, reviewStatus: data.reviewStatus };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// Autopilot Flow Thunks - Used by AutopilotAssessmentFlow and RiskAutopilotV2
// ============================================================================

/**
 * Fetch risk card data (sources, causes, themes, narratives)
 * Used by autopilot to load assessment context
 */
export const fetchRiskCardData = createAsyncThunk(
  'assessments/fetchRiskCardData',
  async ({ assessmentUuid, modelId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/fetchRiskCard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_uuid: modelId,
          assessment_uuid: assessmentUuid,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch risk card data`);
      }

      const data = await response.json();
      return { assessmentUuid, modelId, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Save assessment checkpoint position to database
 * Used by autopilot to persist progress between sessions
 */
export const setAssessmentPosition = createAsyncThunk(
  'assessments/setPosition',
  async ({ modelId, assessmentUuid, position }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'update',
          position,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to save assessment position`);
      }

      const data = await response.json();
      return { assessmentUuid, position, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Save checkpoint without overwriting main position data
 * Persists under position.checkpoint in DB
 */
export const setAssessmentCheckpoint = createAsyncThunk(
  'assessments/setCheckpoint',
  async ({ modelId, assessmentUuid, checkpoint }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'updateCheckpoint',
          checkpoint,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to save assessment checkpoint`);
      }

      const data = await response.json();
      return { assessmentUuid, checkpoint: checkpoint === undefined ? null : checkpoint, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update assessment status with assessmentUuid (autopilot variant)
 * Different from updateAssessmentStatus which uses modelId only
 */
export const updateAssessmentStatusByUuid = createAsyncThunk(
  'assessments/updateStatusByUuid',
  async ({ modelId, assessmentUuid, status, assessmentType = 'builder' }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'updateStatus',
          assessmentStatus: status,
          assessmentType,  // Required to find correct assessment (builder vs explorer)
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to update assessment status`);
      }

      const data = await response.json();
      return { assessmentUuid, status: data.assessmentStatus };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Delete assessment (soft or hard delete)
 * Soft delete archives the assessment for 30 days
 * Hard delete permanently removes all data (Risk Managers only)
 */
export const deleteAssessment = createAsyncThunk(
  'assessments/delete',
  async ({ assessmentUuid, deletionType = 'soft', confirmationText }, { rejectWithValue }) => {
    try {

      const response = await authFetch('/.netlify/functions/deleteAssessment', {
        method: 'POST',
        body: JSON.stringify({
          assessmentUuid,
          deletionType,
          confirmationText
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to delete assessment`);
      }

      const result = await response.json();
      return {
        assessmentUuid,
        deletionType,
        message: result.message
      };
    } catch (error) {
      console.error('[assessmentsSlice] deleteAssessment error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Restore soft-deleted assessment
 * Can only restore within 30 days of deletion
 */
export const restoreAssessment = createAsyncThunk(
  'assessments/restore',
  async ({ assessmentUuid }, { rejectWithValue }) => {
    try {

      const response = await authFetch('/.netlify/functions/restoreAssessment', {
        method: 'POST',
        body: JSON.stringify({ assessmentUuid })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to restore assessment`);
      }

      const result = await response.json();

      // After restoring, fetch the updated assessment data
      // This ensures we have the latest state with deleted_at = NULL
      return {
        assessmentUuid,
        message: result.message
      };
    } catch (error) {
      console.error('[assessmentsSlice] restoreAssessment error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  byId: {},
  loading: {
    all: false,     // fetchAllAssessments loading state
    single: false,  // fetchSingleAssessment loading state
  },
  error: {
    all: null,      // fetchAllAssessments error
    single: null,   // fetchSingleAssessment error
  },
  loadingMessage: '', // Global loading message for UI feedback
};

// Slice
const assessmentsSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    // Synchronous reducers for local state updates
    updateRiskFormData: (state, action) => {
      const { assessmentUuid, riskArea, data } = action.payload;
      if (!state.byId[assessmentUuid]) {
        state.byId[assessmentUuid] = { riskFormData: {} };
      }
      if (!state.byId[assessmentUuid].riskFormData) {
        state.byId[assessmentUuid].riskFormData = {};
      }
      state.byId[assessmentUuid].riskFormData[riskArea] = {
        ...state.byId[assessmentUuid].riskFormData[riskArea],
        ...data,
      };
    },

    updateAssessmentContext: (state, action) => {
      const { assessmentUuid, section, data } = action.payload;
      if (!state.byId[assessmentUuid]) {
        state.byId[assessmentUuid] = { context: {} };
      }
      if (!state.byId[assessmentUuid].context) {
        state.byId[assessmentUuid].context = {};
      }
      state.byId[assessmentUuid].context[section] = {
        ...state.byId[assessmentUuid].context[section],
        ...data,
      };
    },

    addSavedRisk: (state, action) => {
      const { assessmentUuid, risk } = action.payload;
      if (!state.byId[assessmentUuid]) {
        state.byId[assessmentUuid] = { savedRisks: [] };
      }
      if (!state.byId[assessmentUuid].savedRisks) {
        state.byId[assessmentUuid].savedRisks = [];
      }
      state.byId[assessmentUuid].savedRisks.push(risk);
    },

    updateSavedRisk: (state, action) => {
      const { assessmentUuid, riskId, updates } = action.payload;
      if (state.byId[assessmentUuid]?.savedRisks) {
        const riskIndex = state.byId[assessmentUuid].savedRisks.findIndex(
          (r) => r.risk_id === riskId
        );
        if (riskIndex !== -1) {
          state.byId[assessmentUuid].savedRisks[riskIndex] = {
            ...state.byId[assessmentUuid].savedRisks[riskIndex],
            ...updates,
          };
        }
      }
    },

    removeSavedRisk: (state, action) => {
      const { assessmentUuid, riskId } = action.payload;
      if (state.byId[assessmentUuid]?.savedRisks) {
        state.byId[assessmentUuid].savedRisks = state.byId[assessmentUuid].savedRisks.filter(
          (r) => r.risk_id !== riskId
        );
      }
    },

    updateSecurityScenarios: (state, action) => {
      const { assessmentUuid, scenarios } = action.payload;
      if (!state.byId[assessmentUuid]) {
        state.byId[assessmentUuid] = { security: {} };
      }
      if (!state.byId[assessmentUuid].security) {
        state.byId[assessmentUuid].security = {};
      }
      state.byId[assessmentUuid].security.scenarios = scenarios;
    },

    updateSecurityControls: (state, action) => {
      const { assessmentUuid, controls } = action.payload;
      if (!state.byId[assessmentUuid]) {
        state.byId[assessmentUuid] = { security: {} };
      }
      if (!state.byId[assessmentUuid].security) {
        state.byId[assessmentUuid].security = {};
      }
      state.byId[assessmentUuid].security.securityControls = controls;
    },

    updateResidualRisk: (state, action) => {
      const { assessmentUuid, riskId, data } = action.payload;
      if (!state.byId[assessmentUuid]) {
        state.byId[assessmentUuid] = { residualRisk: {} };
      }
      if (!state.byId[assessmentUuid].residualRisk) {
        state.byId[assessmentUuid].residualRisk = {};
      }
      state.byId[assessmentUuid].residualRisk[riskId] = {
        ...state.byId[assessmentUuid].residualRisk[riskId],
        ...data,
      };
    },

    clearAssessment: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byId[assessmentUuid];
      delete state.loading[assessmentUuid];
      delete state.error[assessmentUuid];
    },

    /**
     * Optimistic update for assessment position - updates RTK state immediately
     * without waiting for API call. Used by autopilot flow to keep progress ring in sync.
     */
    updateAssessmentPositionLocal: (state, action) => {
      const { assessmentUuid, position, currentStep, assessmentStatus, assessmentName } = action.payload;
      if (state.byId[assessmentUuid]) {
        if (position !== undefined) {
          state.byId[assessmentUuid].position = position;
        }
        if (currentStep !== undefined) {
          state.byId[assessmentUuid].current_step = currentStep;
        }
        if (assessmentStatus !== undefined && state.byId[assessmentUuid].info) {
          state.byId[assessmentUuid].info.status = assessmentStatus;
        }
        if (assessmentName !== undefined && state.byId[assessmentUuid].info) {
          state.byId[assessmentUuid].info.assessment_name = assessmentName;
        }
      }
    },

    /**
     * Initialize/update assessment data locally (merges with existing state)
     * Used by autopilot and canvas to set up assessment state
     * IMPORTANT: This MERGES data - it does NOT overwrite existing fields
     */
    initializeAssessmentLocal: (state, action) => {
      const {
        assessmentUuid, modelId, modelName, modelType, modelStage,
        useCaseShortDescription, created_at, created_by, avatar, createdByName,
        assessmentStatus, currentStep, templateId, mode, autonomous, position,
        ownerName, ownerEmail, lastUpdatedByName, lastUpdatedByEmail, reviewStatus, updatedAt,
        templateConfig, assessmentName
      } = action.payload;

      if (assessmentUuid) {
        // Get existing state or create empty structure
        const existing = state.byId[assessmentUuid] || {};
        const existingInfo = existing.info || {};

        // MERGE: Only update fields that are explicitly provided (not undefined)
        state.byId[assessmentUuid] = {
          ...existing,
          info: {
            ...existingInfo,
            assessment_uuid: assessmentUuid,
            ...(assessmentName !== undefined && { assessment_name: assessmentName }),
            ...(assessmentStatus !== undefined && { status: assessmentStatus }),
            ...(templateId !== undefined && { template_id: templateId }),
            ...(mode !== undefined && { mode: mode }),
            ...(autonomous !== undefined && { autonomous: autonomous }),
            ...(created_at !== undefined && { created_at: created_at }),
            ...(created_by !== undefined && { created_by: created_by }),
            ...(createdByName !== undefined && { created_by_name: createdByName }),
            ...(avatar !== undefined && { avatar: avatar }),
            ...(updatedAt !== undefined && { updated_at: updatedAt }),
            ...(lastUpdatedByName !== undefined && { last_updated_by_name: lastUpdatedByName }),
            ...(lastUpdatedByEmail !== undefined && { last_updated_by_email: lastUpdatedByEmail }),
          },
          ...(modelId !== undefined && { model_uuid: modelId }),
          ...(modelName !== undefined && { model_name: modelName }),
          ...(modelType !== undefined && { model_type: modelType }),
          ...(modelStage !== undefined && { model_stage: modelStage }),
          ...(useCaseShortDescription !== undefined && { use_case_short_description: useCaseShortDescription }),
          ...(position !== undefined && { position: position }),
          ...(currentStep !== undefined && { current_step: currentStep }),
          ...(ownerName !== undefined && { owner_name: ownerName }),
          ...(ownerEmail !== undefined && { owner_email: ownerEmail }),
          ...(reviewStatus !== undefined && { review_status: reviewStatus }),
          ...(templateConfig !== undefined && { templateConfig: templateConfig }),
          // Preserve existing nested objects, or create empty ones
          context: existing.context || {},
          riskFormData: existing.riskFormData || {},
          savedRisks: existing.savedRisks || [],
          security: existing.security || {},
          residualRisk: existing.residualRisk || {},
        };
      }
    },
  },

  extraReducers: (builder) => {
    // Fetch all assessments
    builder
      .addCase(fetchAllAssessments.pending, (state) => {
        state.loading.all = true;
        state.error.all = null;
      })
      .addCase(fetchAllAssessments.fulfilled, (state, action) => {
        const assessments = action.payload;
        // Populate byId with all assessments
        assessments.forEach(assessment => {
          state.byId[assessment.assessment_uuid] = {
            info: {
              assessment_uuid: assessment.assessment_uuid,
              assessment_name: assessment.assessment_name,
              assessment_description: assessment.assessment_description,
              status: assessment.status,
              template_id: assessment.template_id,
              mode: assessment.mode,
              autonomous: assessment.autonomous,
              edition: assessment.edition,
              created_at: assessment.created_at,
              created_by: assessment.created_by,
              created_by_name: assessment.created_by_name,
              avatar: assessment.avatar,
              updated_at: assessment.updated_at,
              last_updated_by: assessment.last_updated_by,
              last_updated_by_name: assessment.last_updated_by_name,
              last_updated_by_email: assessment.last_updated_by_email,
              last_activity_at: assessment.last_activity_at,
              last_activity_by_name: assessment.last_activity_by_name,
              last_activity_by_email: assessment.last_activity_by_email,
              last_activity_avatar: assessment.last_activity_avatar,
              progress: assessment.progress,
            },
            model_uuid: assessment.model_uuid,
            model_name: assessment.model_name,
            model_type: assessment.model_type,
            model_stage: assessment.model_stage,
            use_case_short_description: assessment.use_case_short_description,
            position: assessment.position,
            current_step: assessment.current_step,
            review_status: assessment.review_status,
            submitted_for_review_at: assessment.submitted_for_review_at,
            review_started_by: assessment.review_started_by,
            review_started_by_name: assessment.review_started_by_name,
            review_started_at: assessment.review_started_at,
            reviewed_at: assessment.reviewed_at,
            reviewed_by: assessment.reviewed_by,
            reviewed_by_name: assessment.reviewed_by_name,
            // Assessment type: 'builder' (technical/AI risks) or 'explorer' (business/societal impacts)
            assessment_type: assessment.assessment_type || 'builder',
            // For Explorer assessments, reference to parent Builder assessment
            parent_assessment_uuid: assessment.parent_assessment_uuid || null,
            // Owner tracking - both object and individual fields
            owner: assessment.owner,
            owner_id: assessment.owner_id,
            owner_email: assessment.owner_email,
            owner_name: assessment.owner_name,
            // These will be populated when needed
            context: {},
            riskFormData: {},
            savedRisks: [],
            security: {},
            residualRisk: {},
          };
        });
        state.loading.all = false;
      })
      .addCase(fetchAllAssessments.rejected, (state, action) => {
        state.loading.all = false;
        state.error.all = action.payload;
      });

    // Fetch single assessment
    builder
      .addCase(fetchSingleAssessment.pending, (state) => {
        state.loading.single = true;
      })
      .addCase(fetchSingleAssessment.fulfilled, (state, action) => {
        state.loading.single = false;

        const assessment = action.payload;
        if (assessment && assessment.assessment_uuid) {
          // Update the specific assessment in state
          state.byId[assessment.assessment_uuid] = {
            info: {
              assessment_uuid: assessment.assessment_uuid,
              assessment_name: assessment.assessment_name,
              assessment_description: assessment.assessment_description,
              status: assessment.status,
              template_id: assessment.template_id,
              mode: assessment.mode,
              autonomous: assessment.autonomous,
              edition: assessment.edition,
              created_at: assessment.created_at,
              created_by: assessment.created_by,
              created_by_name: assessment.created_by_name,
              avatar: assessment.avatar,
              updated_at: assessment.updated_at,
              last_updated_by: assessment.last_updated_by,
              last_updated_by_name: assessment.last_updated_by_name,
              last_updated_by_email: assessment.last_updated_by_email,
              last_activity_at: assessment.last_activity_at,
              last_activity_by_name: assessment.last_activity_by_name,
              last_activity_by_email: assessment.last_activity_by_email,
              last_activity_avatar: assessment.last_activity_avatar,
              progress: assessment.progress,
            },
            model_uuid: assessment.model_uuid,
            model_name: assessment.model_name,
            model_type: assessment.model_type,
            model_stage: assessment.model_stage,
            use_case_short_description: assessment.use_case_short_description,
            position: assessment.position,
            current_step: assessment.current_step,
            review_status: assessment.review_status,
            submitted_for_review_at: assessment.submitted_for_review_at,
            review_started_at: assessment.review_started_at,
            review_started_by: assessment.review_started_by,
            reviewed_at: assessment.reviewed_at,
            reviewed_by: assessment.reviewed_by,
            assessment_type: assessment.assessment_type || 'builder',
            parent_assessment_uuid: assessment.parent_assessment_uuid || null,
            owner: assessment.owner,
            owner_id: assessment.owner_id,
            owner_email: assessment.owner_email,
            owner_name: assessment.owner_name,
            // Preserve existing nested data if already loaded
            context: state.byId[assessment.assessment_uuid]?.context || {},
            riskFormData: state.byId[assessment.assessment_uuid]?.riskFormData || {},
            savedRisks: state.byId[assessment.assessment_uuid]?.savedRisks || [],
            security: state.byId[assessment.assessment_uuid]?.security || {},
            residualRisk: state.byId[assessment.assessment_uuid]?.residualRisk || {},
          };

          console.log('[assessmentsSlice] Single assessment refreshed:', assessment.assessment_uuid);
        }
      })
      .addCase(fetchSingleAssessment.rejected, (state, action) => {
        state.loading.single = false;
        state.error.single = action.payload;
        console.error('[assessmentsSlice] Failed to fetch single assessment:', action.payload);
      });

    // Initialize single assessment
    builder
      .addCase(initializeAssessment.pending, (state, action) => {
        const { modelId } = action.meta.arg;
        state.loading[modelId] = true;
        state.error[modelId] = null;
      })
      .addCase(initializeAssessment.fulfilled, (state, action) => {
        const { assessmentUuid, data } = action.payload;
        state.byId[assessmentUuid] = {
          info: {
            assessment_uuid: assessmentUuid,
            status: data.assessmentStatus,
            template_id: data.templateId,
            mode: data.mode,
            autonomous: data.autonomous,
            created_at: data.createdAt,
            updated_at: data.updatedAt,
          },
          model_uuid: data.model_uuid || null,
          position: data.position,
          current_step: data.currentStep,
          owner: data.owner,
          context: {},
          riskFormData: {},
          savedRisks: [],
          security: {},
          residualRisk: {},
        };
        state.loading[assessmentUuid] = false;
      })
      .addCase(initializeAssessment.rejected, (state, action) => {
        const { modelId } = action.meta.arg;
        state.loading[modelId] = false;
        state.error[modelId] = action.payload;
      });

    // NOTE: Context, risks, security scenarios, and residual risk handlers
    // will be added when the corresponding API endpoints are implemented.
    // For now, these are managed through synchronous reducers.

    // Create assessment
    builder
      .addCase(createAssessment.pending, (state) => {
        state.loading.create = true;
        state.error.create = null;
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        const { assessmentUuid, modelId, assessmentName, templateId, mode, autonomous, currentStep, position, assessmentStatus, assessmentType, parentAssessmentUuid } = action.payload;
        state.byId[assessmentUuid] = {
          info: {
            assessment_uuid: assessmentUuid,
            assessment_name: assessmentName,
            status: assessmentStatus || 'On-Going',
            template_id: templateId,
            mode: mode,
            autonomous: autonomous,
            edition: 'developer',
          },
          model_uuid: modelId,
          position: position || { majorStep: 'useCase' },
          current_step: currentStep || 'Not Started',
          // Assessment type: 'builder' (technical/AI risks) or 'explorer' (business/societal impacts)
          assessment_type: assessmentType || 'builder',
          // For Explorer assessments, reference to parent Builder assessment
          parent_assessment_uuid: parentAssessmentUuid || null,
          context: {},
          riskFormData: {},
          savedRisks: [],
          security: {},
          residualRisk: {},
        };
        state.loading.create = false;
      })
      .addCase(createAssessment.rejected, (state, action) => {
        state.loading.create = false;
        state.error.create = action.payload;
      });

    // Update assessment status
    builder
      .addCase(updateAssessmentStatus.fulfilled, (state, action) => {
        const { modelId, assessmentStatus } = action.payload;
        // Find assessment by modelId and update status
        const assessment = Object.values(state.byId).find(a => a.model_uuid === modelId);
        if (assessment && state.byId[assessment.info?.assessment_uuid]) {
          state.byId[assessment.info.assessment_uuid].info.status = assessmentStatus;
        }
      });

    // Update assessment progress
    builder
      .addCase(updateAssessmentProgress.fulfilled, (state, action) => {
        const { assessmentUuid, currentStep, position, assessmentStatus } = action.payload;
        if (state.byId[assessmentUuid]) {
          if (currentStep !== undefined) {
            state.byId[assessmentUuid].current_step = currentStep;
          }
          if (position !== undefined) {
            state.byId[assessmentUuid].position = position;
          }
          if (assessmentStatus !== undefined && state.byId[assessmentUuid].info) {
            state.byId[assessmentUuid].info.status = assessmentStatus;
          }
        }
      });

    // Submit for review
    builder
      .addCase(submitForReview.fulfilled, (state, action) => {
        const { modelId, reviewStatus } = action.payload;
        const assessment = Object.values(state.byId).find(a => a.model_uuid === modelId);
        if (assessment && state.byId[assessment.info?.assessment_uuid]) {
          state.byId[assessment.info.assessment_uuid].review_status = reviewStatus;
          state.byId[assessment.info.assessment_uuid].submitted_for_review_at = new Date().toISOString();
        }
      });

    // Update review status
    builder
      .addCase(updateReviewStatus.fulfilled, (state, action) => {
        const { modelId, reviewStatus } = action.payload;
        const assessment = Object.values(state.byId).find(a => a.model_uuid === modelId);
        if (assessment && state.byId[assessment.info?.assessment_uuid]) {
          state.byId[assessment.info.assessment_uuid].review_status = reviewStatus;
          state.byId[assessment.info.assessment_uuid].reviewed_at = new Date().toISOString();
        }
      });

    // ============================================================================
    // Autopilot Flow Thunks - extraReducers
    // ============================================================================

    // fetchRiskCardData - Store risk card data in assessment
    builder
      .addCase(fetchRiskCardData.pending, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[assessmentUuid] = true;
        state.error[assessmentUuid] = null;
      })
      .addCase(fetchRiskCardData.fulfilled, (state, action) => {
        const { assessmentUuid, data } = action.payload;
        if (!state.byId[assessmentUuid]) {
          state.byId[assessmentUuid] = { context: {}, riskFormData: {} };
        }
        // Store risk card data (sources, causes, narratives, themes)
        state.byId[assessmentUuid].riskCardData = data;
        state.loading[assessmentUuid] = false;
      })
      .addCase(fetchRiskCardData.rejected, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[assessmentUuid] = false;
        state.error[assessmentUuid] = action.payload;
      });

    // setAssessmentPosition - Update position in state
    builder
      .addCase(setAssessmentPosition.pending, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[`position_${assessmentUuid}`] = true;
      })
      .addCase(setAssessmentPosition.fulfilled, (state, action) => {
        const { assessmentUuid, position } = action.payload;
        if (state.byId[assessmentUuid]) {
          state.byId[assessmentUuid].position = position;
        }
        state.loading[`position_${assessmentUuid}`] = false;
      })
      .addCase(setAssessmentPosition.rejected, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[`position_${assessmentUuid}`] = false;
        state.error[`position_${assessmentUuid}`] = action.payload;
      });

    // setAssessmentCheckpoint - Update checkpoint inside position
    builder
      .addCase(setAssessmentCheckpoint.pending, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[`checkpoint_${assessmentUuid}`] = true;
      })
      .addCase(setAssessmentCheckpoint.fulfilled, (state, action) => {
        const { assessmentUuid, checkpoint } = action.payload;
        if (!state.byId[assessmentUuid]) {
          state.byId[assessmentUuid] = { position: {} };
        }
        if (!state.byId[assessmentUuid].position) {
          state.byId[assessmentUuid].position = {};
        }
        if (checkpoint === null) {
          delete state.byId[assessmentUuid].position.checkpoint;
        } else {
          state.byId[assessmentUuid].position.checkpoint = checkpoint;
        }
        state.loading[`checkpoint_${assessmentUuid}`] = false;
      })
      .addCase(setAssessmentCheckpoint.rejected, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[`checkpoint_${assessmentUuid}`] = false;
        state.error[`checkpoint_${assessmentUuid}`] = action.payload;
      });

    // updateAssessmentStatusByUuid - Update status by assessmentUuid
    builder
      .addCase(updateAssessmentStatusByUuid.pending, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[`status_${assessmentUuid}`] = true;
      })
      .addCase(updateAssessmentStatusByUuid.fulfilled, (state, action) => {
        const { assessmentUuid, status } = action.payload;
        if (state.byId[assessmentUuid]?.info) {
          state.byId[assessmentUuid].info.status = status;
        }
        state.loading[`status_${assessmentUuid}`] = false;
      })
      .addCase(updateAssessmentStatusByUuid.rejected, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        state.loading[`status_${assessmentUuid}`] = false;
        state.error[`status_${assessmentUuid}`] = action.payload;
      });

    // ============================================================================
    // Review Flow Integration - Sync review state changes with assessment data
    // ============================================================================

    // startReview - Update assessment when review session starts
    builder.addCase(startReview.fulfilled, (state, action) => {
      const { assessmentUuid, reviewStatus, startedBy, startedByName, startedAt } = action.payload;
      if (state.byId[assessmentUuid]) {
        state.byId[assessmentUuid].review_status = reviewStatus;
        state.byId[assessmentUuid].review_started_by = startedBy;
        state.byId[assessmentUuid].review_started_by_name = startedByName;
        state.byId[assessmentUuid].review_started_at = startedAt;
      }
    });

    // approveReview - Update assessment when review approved
    builder.addCase(approveReview.fulfilled, (state, action) => {
      const { assessmentUuid, reviewStatus, reviewedBy, reviewedByName, reviewedAt } = action.payload;
      if (state.byId[assessmentUuid]) {
        state.byId[assessmentUuid].review_status = reviewStatus;
        state.byId[assessmentUuid].reviewed_by = reviewedBy;
        state.byId[assessmentUuid].reviewed_by_name = reviewedByName;
        state.byId[assessmentUuid].reviewed_at = reviewedAt;
      }
    });

    // rejectReview - Update assessment when review rejected
    builder.addCase(rejectReview.fulfilled, (state, action) => {
      const { assessmentUuid, reviewStatus, reviewedBy, reviewedByName, reviewedAt } = action.payload;
      if (state.byId[assessmentUuid]) {
        state.byId[assessmentUuid].review_status = reviewStatus;
        state.byId[assessmentUuid].reviewed_by = reviewedBy;
        state.byId[assessmentUuid].reviewed_by_name = reviewedByName;
        state.byId[assessmentUuid].reviewed_at = reviewedAt;
      }
    });

    // ============================================================================
    // Delete/Restore Assessment - Soft delete and restore functionality
    // ============================================================================

    // deleteAssessment - Remove from state on successful deletion
    builder
      .addCase(deleteAssessment.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(deleteAssessment.fulfilled, (state, action) => {
        const { assessmentUuid, deletionType } = action.payload;
        // Remove from state (UI will hide it)
        delete state.byId[assessmentUuid];
        state.loading.delete = false;
        console.log(`[assessmentsSlice] Assessment ${assessmentUuid} deleted (${deletionType})`);
      })
      .addCase(deleteAssessment.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete = action.payload;
        console.error('[assessmentsSlice] Delete assessment failed:', action.payload);
      });

    // restoreAssessment - Trigger refetch to reload restored assessment
    builder
      .addCase(restoreAssessment.pending, (state) => {
        state.loading.restore = true;
        state.error.restore = null;
      })
      .addCase(restoreAssessment.fulfilled, (state, action) => {
        const { assessmentUuid } = action.payload;
        state.loading.restore = false;
        console.log(`[assessmentsSlice] Assessment ${assessmentUuid} restored - refetch to see it`);
        // Note: Assessment will reappear after fetchAllAssessments() is called
      })
      .addCase(restoreAssessment.rejected, (state, action) => {
        state.loading.restore = false;
        state.error.restore = action.payload;
        console.error('[assessmentsSlice] Restore assessment failed:', action.payload);
      });
  },
});

export const {
  updateRiskFormData,
  updateAssessmentContext,
  addSavedRisk,
  updateSavedRisk,
  removeSavedRisk,
  updateSecurityScenarios,
  updateSecurityControls,
  updateResidualRisk,
  clearAssessment,
  updateAssessmentPositionLocal,
  initializeAssessmentLocal,
} = assessmentsSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectAssessmentsById = (state) => state.assessments.byId;
export const selectAssessmentsLoading = (state) => state.assessments.loading;
export const selectAssessmentsError = (state) => state.assessments.error;
export const selectLoadingMessage = (state) => state.assessments.loadingMessage;

export const selectAssessmentById = (state, assessmentUuid) =>
  state.assessments.byId[assessmentUuid];

export const selectAllAssessments = (state) =>
  Object.values(state.assessments.byId);

export const selectAssessmentsCount = (state) =>
  Object.keys(state.assessments.byId).length;

export const selectAssessmentByModelId = (state, modelId) =>
  Object.values(state.assessments.byId).find(a => a.model_uuid === modelId);

export const selectAssessmentUuidByModelId = (state, modelId) => {
  const assessment = Object.values(state.assessments.byId).find(a => a.model_uuid === modelId);
  return assessment?.info?.assessment_uuid || null;
};

// Assessments with specific status
export const selectAssessmentsByStatus = (state, status) =>
  Object.values(state.assessments.byId).filter(a => a.info?.status === status);

// Assessments pending review
export const selectAssessmentsPendingReview = (state) =>
  Object.values(state.assessments.byId).filter(a => a.review_status === 'submitted');

// Assessments by edition
export const selectAssessmentsByEdition = (state, edition) =>
  Object.values(state.assessments.byId).filter(a => a.info?.edition === edition);

// ============================================================================
// Builder/Explorer Assessment Type Selectors
// ============================================================================

// Select all Builder assessments (technical safety, security, AI-specific risks)
export const selectBuilderAssessments = (state) =>
  Object.values(state.assessments.byId).filter(a =>
    a.assessment_type === 'builder' || !a.assessment_type
  );

// Select all Builder (use_case) assessments
export const selectUseCaseAssessments = (state) =>
  Object.values(state.assessments.byId).filter(a => a.assessment_type === 'use_case');

// Select all Explorer/business_case assessments (supports both legacy 'explorer' and new 'business_case')
export const selectBusinessCaseAssessments = (state) =>
  Object.values(state.assessments.byId).filter(a =>
    a.assessment_type === 'business_case' || a.assessment_type === 'explorer'
  );

// Alias: selectExplorerAssessments → selectBusinessCaseAssessments for backward compat
export const selectExplorerAssessments = selectBusinessCaseAssessments;

// Select Builder (use_case) assessments for a specific Builder
export const selectUseCaseAssessmentsByBuilder = (state, builderUuid) =>
  Object.values(state.assessments.byId).filter(a =>
    a.assessment_type === 'use_case' && a.parent_assessment_uuid === builderUuid
  );

// Select Explorer (business_case) assessments for a specific Builder
export const selectBusinessCaseAssessmentsByUseCase = (state, useCaseUuid) =>
  Object.values(state.assessments.byId).filter(a =>
    (a.assessment_type === 'business_case' || a.assessment_type === 'explorer') &&
    a.parent_assessment_uuid === useCaseUuid
  );

// Select Explorer assessments for a specific model (backward compat)
export const selectExplorerAssessmentsByModel = (state, modelId) =>
  Object.values(state.assessments.byId).filter(a =>
    (a.assessment_type === 'business_case' || a.assessment_type === 'explorer') &&
    a.model_uuid === modelId
  );

// Select completed Builder assessments (for Builder creation dialog)
export const selectCompletedBuilderAssessments = (state) =>
  Object.values(state.assessments.byId).filter(a =>
    (a.assessment_type === 'builder' || !a.assessment_type) &&
    a.info?.status === 'Completed'
  );

// Select completed Builder (use_case) assessments (for Explorer creation dialog)
export const selectCompletedUseCaseAssessments = (state) =>
  Object.values(state.assessments.byId).filter(a =>
    a.assessment_type === 'use_case' &&
    a.info?.status === 'Completed'
  );

// Check if a model has a Builder assessment and get its status + navigator count
export const selectModelBuilderStatus = (state, modelId) => {
  const builderAssessments = Object.values(state.assessments.byId).filter(a =>
    (a.assessment_type === 'builder' || !a.assessment_type) && a.model_uuid === modelId
  );

  if (builderAssessments.length === 0) {
    return { hasBuilder: false, isCompleted: false, isInProgress: false, assessment: null, navigatorCount: 0 };
  }

  const builder = builderAssessments[0];
  const builderUuid = builder.info?.assessment_uuid;

  // Count navigators linked to this builder
  const navigatorCount = builderUuid
    ? Object.values(state.assessments.byId).filter(a =>
        a.assessment_type === 'use_case' && a.parent_assessment_uuid === builderUuid
      ).length
    : 0;

  return {
    hasBuilder: true,
    isCompleted: builder.info?.status === 'Completed',
    isInProgress: builder.info?.status === 'On-Going',
    assessment: builder,
    navigatorCount
  };
};

// Get all model IDs that have Builder assessments
export const selectModelIdsWithBuilderAssessments = (state) => {
  const builderAssessments = Object.values(state.assessments.byId).filter(a =>
    a.assessment_type === 'builder' || !a.assessment_type
  );
  return new Set(builderAssessments.map(a => a.model_uuid).filter(Boolean));
};

// ============================================================================
// Review-Specific Selectors
// ============================================================================

// Select assessments by review status
export const selectAssessmentsByReviewStatus = createSelector(
  [selectAllAssessments, (_, status) => status],
  (assessments, status) => assessments.filter(a => a.review_status === status)
);

// Select assessments awaiting review (for Risk Managers)
export const selectAssessmentsAwaitingReview = createSelector(
  selectAllAssessments,
  assessments => assessments.filter(a => a.review_status === 'submitted')
);

// Select in-progress reviews for current user
export const selectInProgressReviews = createSelector(
  [selectAllAssessments, (_, userEmail) => userEmail],
  (assessments, userEmail) =>
    assessments.filter(a =>
      a.review_status === 'in_progress' &&
      a.review_started_by === userEmail
    )
);

// Get review session info for assessment
export const selectReviewSessionInfo = createSelector(
  [(state, assessmentUuid) => state.assessments.byId[assessmentUuid]],
  (assessment) => {
    if (!assessment) return null;

    return {
      status: assessment.review_status,
      startedBy: assessment.review_started_by_name || assessment.review_started_by,
      startedAt: assessment.review_started_at,
      reviewedBy: assessment.reviewed_by_name || assessment.reviewed_by,
      reviewedAt: assessment.reviewed_at,
      submittedAt: assessment.submitted_for_review_at,
      canResume: assessment.review_status === 'in_progress',
      isCompleted: assessment.review_status === 'approved' || assessment.review_status === 'rejected',
      isPending: assessment.review_status === 'submitted'
    };
  }
);

export default assessmentsSlice.reducer;
